import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  TextField,
} from "@mui/material";
import { CircularProgress } from "@mui/material";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  useCompanySteps,
  useHandlePageTraversal,
} from "../../../../common/hooks";
import { HookedDatePicker } from "../../../components/HookedDatePicker";
import { HookedForm } from "../../../components/HookedForm";
import { HookedRadioGroup } from "../../../components/HookedRadioGroup";
import { CompaniesStep, RoutePaths } from "../../../consts/enums";
import { HookedField } from "../../../../common/components/HookedField";
import { EventName, trackEvent } from "../../../../common/utils/helpers";

export const SellerDetails = () => {
  const { id: startupId } = useParams();
  if (startupId === undefined) {
    return <Navigate to="/dashboard" replace={true} />;
  }
  const { data, isLoading, isError } = useCompanySteps(
    CompaniesStep.STARTUP,
    startupId
  );
  const navigate = useNavigate();
  const formHook = useForm();
  const { handleNext } = useHandlePageTraversal(formHook, Number(startupId));

  useEffect(() => {
    if (data && !isLoading) {
      formHook.reset({ ...data });
    }
  }, [data, isLoading]);

  if (isError) {
    return <Navigate to="/dashboard" replace={true} />;
  }
  if (!data) return <CircularProgress />;

  return (
    <Box
      sx={{
        backgroundColor: "common.black",
        backgroundImage: "url(/images/BiggerGradient.png)",
        backgroundSize: "cover",
        backgroundPosition: "right",
        backgroundRepeat: "no-repeat",
        width: "100%",
        minHeight: "100%",
      }}
    >
      <Container maxWidth="md" sx={{ padding: 4 }}>
        <Typography variant="h3" sx={{ color: "white" }}>
          PUBLIC INFORMATION
        </Typography>
        <Typography variant="subtitle1" sx={{ color: "common.grayOpacity" }}>
          Other Foundy users can view these details
        </Typography>
        <Paper
          variant="outlined"
          elevation={2}
          sx={{ marginTop: 4, padding: 3 }}
        >
          <HookedForm formHook={formHook}>
            <Typography variant="h3" sx={{ marginBottom: 1 }}>
              Team
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "common.gray", marginBottom: 2 }}
            >
              How many full time employees do you have?
            </Typography>
            <HookedRadioGroup
              name="startup_team_size"
              options={[
                {
                  value: "1",
                  label: "1 to 3",
                },
                {
                  value: "4",
                  label: "4 to 10",
                },
                {
                  value: "11",
                  label: "11 to 50",
                },
                {
                  value: "50",
                  label: "50+",
                },
              ]}
            />
            <Box
              component="hr"
              sx={{
                width: "100%",
                borderColor: "common.lightgray",
                borderStyle: "solid",
                marginY: 2,
              }}
            />
            <Typography variant="h3" sx={{ marginBottom: 1 }}>
              Date founded
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "common.gray", marginBottom: 2 }}
            >
              When was your company founded?
            </Typography>
            <HookedDatePicker
              hiddenLabel
              format="LLLL/yyyy"
              name="date_founded"
              variant="filled"
              defaultValue={data.date_founded}
            />
            <Box
              component="hr"
              sx={{
                width: "100%",
                borderColor: "common.lightgray",
                borderStyle: "solid",
                marginY: 2,
              }}
            />
            <Typography variant="h3" sx={{ marginBottom: 2, marginTop: 2 }}>
              Who are your competitors?
            </Typography>
            <HookedField
              fullWidth
              component={TextField}
              variant="outlined"
              name="competitors"
              placeholder="Type here"
            />
            <Box
              component="hr"
              sx={{
                width: "100%",
                borderColor: "common.lightgray",
                borderStyle: "solid",
                marginY: 2,
              }}
            />
            <Typography variant="h3" sx={{ marginBottom: 2, marginTop: 2 }}>
              Provide a brief overview of your company to best promote it to our
              buyer network
            </Typography>
            <HookedField
              fullWidth
              component={TextField}
              variant="outlined"
              name="description"
              placeholder="Type here"
              multiline={true}
              minRows="10"
            />
          </HookedForm>
        </Paper>
        <Box
          sx={{
            textAlign: "right",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() =>
              handleNext(
                RoutePaths.REGISTER_SELLER + `/${startupId}/post-acquisition`
              )
            }
            sx={{ marginTop: 2 }}
            variant="roundsecondary"
          >
            Back
          </Button>
          <Button
            onClick={() =>
              handleNext(RoutePaths.REGISTER_SELLER + `/${startupId}/keyassets`)
            }
            sx={{ marginTop: 2 }}
            variant="roundgradient"
          >
            Save &amp; Continue
          </Button>
        </Box>

        <Box
          sx={{
            display: { xs: "flex", md: "block" },
            flexDirection: "column",
            marginTop: 8,
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            color="white"
            sx={{ marginBottom: 1 }}
          >
            Just want to enter the marketplace?
          </Typography>
          <Typography
            variant="body2"
            color="common.gray"
            sx={{ marginBottom: 2 }}
          >
            Come back later to complete your Seller Profile.
          </Typography>
          <Button
            variant="roundprimary"
            onClick={() => {
              trackEvent(EventName.SKIP_SIGN_UP);
              navigate(RoutePaths.YOU_ACQUISITION);
            }}
          >
            <span>Skip</span>
          </Button>
        </Box>
      </Container>
    </Box>
  );
};
